<template>
  <div class="mf-table" ref="rootEl">
    <o-table
      :data="tableData"
      :backendSorting="apiMode || isLocallyFiltered"
      :backendFiltering="apiMode"
      :mobile-cards="false"
      :loading="isLoading"
      :rowClass="getRowClass"
      :checkable="isCheckable"
      :is-row-checkable="isRowCheckable"
      @update:checked-rows="selectedCheckBoxRows($event)"
      v-model:checked-rows="selectedRows"
      :header-checkable="checkableHeader"
      @sort="onSort"
      :default-sort="getDefaultSortField()"
    >
      <o-table-column
        v-for="field in screenCols"
        :key="field.id"
        :field="field.id"
        :visible="field.visible"
        :label="field.title"
        :width="field.width"
        :sortable="Boolean(field.sortField)"
        :sticky="field.sticky"
        :headerSelectable="true"
        v-bind="field"
      >
        <template #searchable="props">
          <div v-if="selectedBulkAction">
            <button
              class="button has-text-weight-bold has-background-black-bis mr-3 has-text-white"
              @click="initiateBulkAction()"
              :disabled="!selectedRows.length"
            >
              Delete
            </button>
            <button
              class="button is-outlined has-text-weight-bold"
              @click="resetBulkAction(true)"
            >
              Cancel
            </button>
          </div>
        </template>
        <template v-slot="props">
          <component
            v-if="isFieldComponent(field.name)"
            :is="field.name"
            :key="props.row.uuid || props.row._id"
            :row-data="props.row"
            :row-index="props.index"
            :row-field="field"
            :value="getVal(props.row, field)"
          />
          <a v-else-if="field.openCard"
            class="is-text-link text-overflow is-clickable"
            @click.prevent="handleOpenCard(props.row, field)"
          >
              {{ getVal(props.row, field)  }}
          </a>
          <div v-else-if="['useInShipping', 'autoAdd', 'isAdmin', 'default_location', 'isDefault'].includes(field.id)" class="s-align-items-center is-justify-content-center">
            <i class='has-text-weight-bold ' :class="getVal(props.row, field)? 'icon-checkmark': 'has-text-danger icon-close' "></i>
          </div>
           <div v-else-if="checkNestedLocation(props.row, field)"
            :key="field.id"
            class="text-overflow is-flex is-align-items-center is-clickable"
          >
            <i class="icon-attachedlocationsOL has-text-link" @click="$emit('open-modal')" v-tooltip="'Nested Location'"></i> {{ getVal(props.row, field) }}
          </div>
          <div v-else
            :key="field.id"
            class="text-overflow"
          >
            {{ getVal(props.row, field) }}
          </div>
          <data-table-action-fields v-if="field.id === 'action'" 
          :row-data="props.row" 
          :row-field="field" 
          @cell-clicked="tableCellClicked" />
        </template>
      </o-table-column>
      <template #empty>
        <div class="table-footer" v-if="!isLoading">
          <span>{{ noDataMsg }}</span>
        </div>
      </template>
    </o-table>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref, toRefs } from 'vue'
import { useStore } from 'vuex';
import InfiniteScrollUtil from './InfiniteScrollUtil';
import _ from 'lodash';
import { get, isArray, isEmpty, isFunction } from 'lodash';
import CategoryCountIcon from '@/components/fields/CategoryCountIcon.vue';
import DataTableActionFields from './DataTableActionFields.vue';

export default {
  name: 'data-table',
  components: {
    'category-count-icon': CategoryCountIcon,
    'data-table-action-fields': DataTableActionFields, // to maintain backward compatibility as some tables are using this component for action field
  },
  props: {
    apiMode: {
      required: false,
      type: Boolean,
      default: true,
    },
    tableOpts: Object,
    loadData: {
      required: true,
      type: Function,
    },
    checkableHeader: {
      type: Boolean,
      default: true,
    },
    singleSelection: {
      type: Boolean,
      default: false
    },
    selectedBulkAction: {
      type: String,
      default: ''
    },
  },
  emits: ['cell-clicked', 'checkbox-toggled'],
  setup(props, { emit }) {
    const data = reactive({
      tableData: [],
      total: 0,
      isLoading: false,
      dataTableElem: {},
      limit: 25,
      sort: [
        {
          sortField: getDefaultSortField(),
          direction: _.get(props, 'tableOpts.defaultSortOrder', 'asc'),
        },
      ],
      selectedRows: [],
      selectedBulkAction: props.selectedBulkAction,
    })

    function getDefaultSortField() {
      return _.get(props, 'tableOpts.defaultSortField', 'name');
    };

    const rootEl = ref(null);
    const store = useStore();
    const componentFieldNames = ['category-count-icon', 'mf-tag-color-cell'];
    const isFieldComponent = (name) => componentFieldNames.includes(name);

    const screenCols = computed(() => {
      let cols = _.get(props.tableOpts, 'fields', []);
      
      cols = _.map(cols, (f) => {
        f.tdAttrs = () => ({ style: `width: ${f.width}`, class: f.tdClass || '' });
        return f;
      });
      return cols
    })

    // Message to display when no data in table
    const noDataMsg = computed(() => {
      return 'No data to display.';
    });

    const isRowCheckable = (row) => {
      if (_.isFunction(_.get(props, 'tableOpts.isRowCheckable'))) {
        return props.tableOpts.isRowCheckable(row);
      }
      return true;
    };

    const setHeaderHeight = () => {
      if (rootEl.value) {
        // fixed table width
        if (props.tableOpts.stickeyHeaders) {
          const oTableElem = rootEl.value.querySelector('.o-table');
          if (oTableElem) {
            const oTablecols = _.cloneDeep(screenCols.value);
            let activeCols = _.filter(oTablecols, { visible: true });
            if (props.tableOpts.isCardView) {
              activeCols = oTablecols;
            }
            const tableWidth = activeCols?.reduce((total, obj) => {
              const val = Number(obj.width.replace('px', ''));
              return Number(total) + val;
            }, 0);
            oTableElem.style = `width: ${tableWidth}px;`;
            // below code is to enable freezer shadow based on column chooser
            const tableWrapper = rootEl.value.querySelector('.o-table__wrapper--sticky-header');
            if (activeCols.length < 8 && !tableWrapper.classList.contains('ending-sticky-shadow')) {
              tableWrapper.classList.add('ending-sticky-shadow');
            } else if (activeCols.length > 7) {
              tableWrapper.classList.remove('ending-sticky-shadow');
            }
          }
        }
        const tableElem = rootEl.value.getElementsByClassName('o-table__wrapper--sticky-header');
        if (tableElem[0]) {
          const tableHead = tableElem[0].querySelectorAll('thead > tr');
          if (tableHead[1]) {
            tableHead[1].style = `top: ${tableHead[0].getBoundingClientRect().height}px;`;
          }
        }
      }
    };

    const dataManager = async (page = 1, infiniteScroll) => {
      data.isLoading = true;
      if (_.isUndefined(infiniteScroll)) setHeaderHeight();
      data.page = page;
      try {
        const params = {
          limit: data.limit,
          page: data.page,
          search: _.get(store, 'state.queryParams.searchText', ''),
          sort: data.sort,
        };
        if (props.apiMode && props.loadData) {
          if(page === 1){
            data.tableData = [];
          }
          const response = await props.loadData(params);
          if (page === 1) {
            data.tableData = response.data;
          } else {
            data.tableData.push(...response.data);
          }
          data.total = response.total;
          data.isLoading = false;
        } else if (props.isPmCardView) {
          setTimeout(() => {
            data.tableData = props.loadData();
            data.isLoading = false;
          }, 0);
        } else {
          data.tableData = await props.loadData(data.sort);
        }
      } catch (e) {
        console.error('error', e);
        data.tableData = [];
      } finally {
        data.isLoading = false;
        setHeaderHeight();
      }
    };

    const scrollParams = computed(() => _.pick(data, [
      'total',
      'tableData',
      'page',
      'isLoading',
    ]));

    onMounted(async () => {
      await dataManager();
      // handle infinite scroll
      const { setRowElemHeight, handleInfiniteScroll } = InfiniteScrollUtil(
        _.assign(scrollParams.value, { root: rootEl, dataManager }),
      );
      setRowElemHeight();
      
      if (rootEl.value) {
        [data.dataTableElem] = rootEl.value.getElementsByClassName('o-table__wrapper');
      }
      data.dataTableElem.addEventListener('scroll', async (event) => {
        // const {
        //   scrollLeft,
        //   scrollWidth,
        //   offsetWidth,
        // } = event.target;
        // // below code is to provide shadow when scrolling in x-axis.
        // if (scrollWidth) {
        //   if (scrollLeft === 0) {
        //     event.target.classList.remove('starting-sticky-shadow');
        //     event.target.classList.remove('ending-sticky-shadow');
        //   } else {
        //     event.target.classList.add('starting-sticky-shadow');
        //     event.target.classList.remove('ending-sticky-shadow');
        //     // below code to hide the active column filter dropdown on horizontal scroll
        //     const dropdown = document.querySelector('.o-table__th .o-drop__menu--active');
        //     if (!_.isEmpty(dropdown)) store.dispatch('setHideFilterDropdown', true);
        //   }
        //   if ((Math.ceil(scrollLeft) + offsetWidth - 20) >= scrollWidth) {
        //     event.target.classList.add('ending-sticky-shadow');
        //   }
        // }
        
        if (props.apiMode) await handleInfiniteScroll(event, scrollParams.value);
      });
      window.addEventListener('resize', () => {
        setHeaderHeight();
      });
    })

    const checkNestedLocation = (row, field) => {
      return row.nestedLocation && field.id === 'nested-icon';
    };

    const initiateBulkAction = () => {
      emit('initiate-bulk-action', data.selectedRows);
    };

    const isCheckable = computed(() => {
      if (_.isFunction(_.get(props, 'tableOpts.isCheckable'))) {
        return props.tableOpts.isCheckable(data.selectedBulkAction);
      }
      return _.get(props, 'tableOpts.isCheckable', false);
    });
    const resetBulkAction = (resetCheckable) => {
      data.selectedRows = [];
      emit('reset-bulk-action');
    };

    const getVal = (row, field) => {
      const val = _.get(row, field.prop, '');
      if (field.formatter && _.isFunction(field.formatter)) {
        return field.formatter(val)
      }
      if (isArray(val)) {
        return val.join(', ')
      }
      return val;
    }

    const tableCellClicked = (payload) => {
      if (payload?.type === 'three-dot') {
        const dataKey = _.isUndefined(payload.data._id) ? 'uuid' : '_id';
        const tableRow = _.find(data.tableData, { [dataKey]: payload.data[dataKey] });
        _.set(tableRow, 'actionMenuOpen', payload.active);
        return;
      }
      emit('cell-clicked', payload);
    };

    const getRowClass = (row) => {
      if (row) {
        if (row.actionMenuOpen) return 'row-action-open';
      }
      return '';
    };

    const selectedCheckBoxRows = (rows) => {
      if (props.singleSelection && rows.length > 1) {
        data.selectedRows = rows.slice(-1)
      } else {
        data.selectedRows = rows
      }
      emit('checkbox-toggled', data.selectedRows);
    };

  const onSort = async (field, order) => {
      const { sortField } = _.find(screenCols.value, { id: field });
      if (sortField) field = sortField;
      data.sort = [{ sortField: field, direction: order }];
      await dataManager();
    };
   const handleOpenCard = (row, field) => {
    emit('cell-clicked', {
      type: field.isForms ? 'isForms' : 'openCard',
      data: { _id: row._id },
    });
  };
    return {
      ...toRefs(data),
      screenCols,
      rootEl,
      noDataMsg,
      getVal,
      isFieldComponent,
      tableCellClicked,
      getRowClass,
      checkNestedLocation,
      selectedCheckBoxRows,
      onSort,
      getDefaultSortField,
      initiateBulkAction,
      resetBulkAction,
      isCheckable,
      isRowCheckable,
      handleOpenCard,
    }
  }
}
</script>

<style>

</style>